import type { Edge, Graph, LineID } from '@metro/graph'
import { EdgeType } from '@metro/graph'
import { rideLinksOf } from './rideLinks'
import type { AlgorithmConfig } from './config'
import type { Route } from './route'
import { signatureKey } from './route'
import { scoreLess } from './ranking'

/**
 * Keeps the best route (by scoreLess) for each unique signatureKey,
 * eliminating structurally identical routes.
 */
export function dedupBySignature(routes: readonly Route[]): Route[] {
  const keep = new Map<string, Route>()
  for (const r of routes) {
    const k = signatureKey(r)
    const existing = keep.get(k)
    if (!existing || scoreLess(r, existing)) keep.set(k, r)
  }
  return Array.from(keep.values())
}

/**
 * Returns true if candidate time exceeds best * cfg.maxTimeRatio.
 * If best <= 0, the path is accepted.
 */
export function ruleMaxTimeRatio(candidate: number, best: number, cfg: AlgorithmConfig): boolean {
  if (best <= 0) return false
  return candidate > cfg.maxTimeRatio * best
}

/** Returns true if the path contains more transfers than cfg.maxTransfers allows.
 *
 * Match `edgesToRoute`'s user-perceived transferCount semantics: count
 * one transfer per ride→transfer boundary, NOT per raw transfer edge.
 * Consecutive transfers within one hub (e.g. the 4-platform Paddington
 * indoor walk h&c → cir1 → eliz → cir0 → bak) aggregate into a single
 * interchange. Counting raw edges would falsely cap such routes at
 * maxTransfers=4 even though the user only changed lines once.
 */
export function ruleMaxTransfers(edges: readonly Edge[], cfg: AlgorithmConfig): boolean {
  let count = 0
  let prevWasRide = false
  for (const e of edges) {
    if (e.type === EdgeType.Ride) {
      prevWasRide = true
    } else {
      if (prevWasRide) count += 1
      prevWasRide = false
    }
  }
  return count > cfg.maxTransfers
}

/**
 * Returns true if the path contains a ride→transfer→ride sub-sequence
 * where a direct ride edge exists in the graph from the first segment's
 * origin to the third segment's destination.
 */
export function ruleNoIndirectTransferWhenDirectExists(
  edges: readonly Edge[],
  graph: Graph,
): boolean {
  if (edges.length < 3) return false
  for (let i = 0; i <= edges.length - 3; i++) {
    const first = edges[i]
    const mid = edges[i + 1]
    const third = edges[i + 2]
    if (first.type !== EdgeType.Ride) continue
    if (mid.type !== EdgeType.WithinStationTransfer && mid.type !== EdgeType.WalkTransfer) continue
    if (third.type !== EdgeType.Ride) continue
    if (rideLinksOf(graph).connects(first.from.id, third.to.id)) return true
  }
  return false
}

/**
 * Returns true if any walkTransfer edge has a weight below
 * cfg.degenerateWalkBelowSec — degenerate walks indicate graph noise,
 * not a real pedestrian connection.
 */
export function ruleNoDegenerateWalkTransfer(
  edges: readonly Edge[],
  cfg: AlgorithmConfig,
): boolean {
  for (const e of edges) {
    if (e.type === EdgeType.WalkTransfer && e.weight < cfg.degenerateWalkBelowSec) return true
  }
  return false
}

/**
 * Returns true if two STREET walks meet with no ride between them (in-station
 * transfers and walks don't count as rides): walking station → pier →
 * station without boarding the boat. Walk links join river piers to nearby
 * stations over the street (TASK-986); chained, they became a shortcut past a
 * station's own transfer (Westminster Jubilee → pier → District). Walks
 * between the stations of one complex chain freely — Whitechapel District →
 * Windrush → Elizabeth, West Hampstead Thameslink → Mildmay → Jubilee have no
 * direct link. Same rule on iOS / Android.
 */
export function ruleNoChainedWalks(edges: readonly Edge[]): boolean {
  let streetWalksSinceRide = 0
  for (const e of edges) {
    if (e.type === EdgeType.Ride) streetWalksSinceRide = 0
    else if (e.type === EdgeType.WalkTransfer && e.street === true && ++streetWalksSinceRide > 1)
      return true
  }
  return false
}

/**
 * Returns true if the path re-joins a line at a station that is fewer
 * than cfg.minSameLineLoopStops positions away (in the given station
 * order) from where the path left that line.
 */
export function ruleNoSameLineLoop(
  edges: readonly Edge[],
  stationOrder: Record<LineID, readonly string[]>,
  cfg: AlgorithmConfig,
): boolean {
  type Spot = { line: LineID; stop: string }
  const spots: Spot[] = []
  let currentLine: LineID = ''
  for (const e of edges) {
    if (e.type === EdgeType.Ride) {
      if (currentLine !== e.from.lineID && currentLine !== '') {
        spots.push({ line: currentLine, stop: e.from.id })
      }
      currentLine = e.from.lineID
      for (const s of spots) {
        if (s.line === currentLine) {
          const order = stationOrder[currentLine] ?? []
          if (stationDistance(order, s.stop, e.from.id) < cfg.minSameLineLoopStops) return true
        }
      }
    }
    // walkTransfer / withinStationTransfer: no line change
  }
  return false
}

function stationDistance(order: readonly string[], a: string, b: string): number {
  const ia = order.indexOf(a)
  const ib = order.indexOf(b)
  if (ia < 0 || ib < 0) return Number.MAX_SAFE_INTEGER
  return Math.abs(ia - ib)
}

/**
 * Returns true if the path's ride sequence re-enters a line that was
 * previously departed. Catches "ебанный бред" routes that detour onto
 * line B for a stop or two and then re-board line A — a real rider
 * would never do that. Each ride leg's `lineID` is compared against
 * the set of previously-used (and abandoned) lines; if the current ride
 * leg's line is in that set, reject.
 *
 * Within a continuous ride on the same line the line is "in use", not
 * "abandoned" — so this only triggers on RE-board after a transfer
 * away.
 */
export function ruleNoLineReentry(edges: readonly Edge[]): boolean {
  if (edges.length === 0) return false
  const departed = new Set<LineID>()
  let currentLine: LineID | null = null
  for (const e of edges) {
    if (e.type !== EdgeType.Ride) continue
    const line = e.from.lineID
    if (currentLine === null || currentLine === line) {
      currentLine = line
      continue
    }
    // Line change: previous line is now departed.
    departed.add(currentLine)
    if (departed.has(line)) return true
    currentLine = line
  }
  return false
}

/**
 * Returns true if the path visits the same `naptanId` twice via distinct
 * per-line `stationId`s. Catches cluster-revisits that `ruleNoRevisit`
 * misses because the underlying records have different `id`s while
 * sharing the same TfL identifier (e.g. Stratford `central.940GZZLUSTD`
 * and `jubilee.940GZZLUSTD` both carry naptan `940GZZLUSTD`).
 *
 * Stations without a `naptanId` are NOT tracked — multiple `undefined`
 * entries are treated as non-cluster and never collide.
 */
export function ruleNoNaptanIdRevisit(edges: readonly Edge[]): boolean {
  if (edges.length === 0) return false
  const seen = new Set<string>()
  let currentCluster: string | undefined
  const consider = (naptanId: string | undefined): boolean => {
    if (naptanId === undefined) {
      // Stations without naptanId are non-cluster — never collide and
      // never become "current cluster" (we forget previous when leaving).
      currentCluster = undefined
      return false
    }
    if (naptanId === currentCluster) return false
    if (seen.has(naptanId)) return true
    seen.add(naptanId)
    currentCluster = naptanId
    return false
  }
  if (consider(edges[0].from.naptanId)) return true
  for (const e of edges) {
    if (e.type === EdgeType.Ride) {
      if (consider(e.to.naptanId)) return true
    } else if (e.type === EdgeType.WalkTransfer && e.street === true) {
      // STREET edges are landings (canon 2026-07, matches Android):
      // the rider physically arrives at the target station on the
      // street, so walking back into a previously-visited cluster is a
      // revisit even if no ride departs from it.
      if (consider(e.to.naptanId)) return true
    }
    // WithinStationTransfer / non-street WalkTransfer: a hop *within*
    // a hub (e.g. between sibling per-line records) shares the same
    // naptanId, so it's not a re-entry — the next ride's `to.naptanId`
    // governs whether we've revisited a previously-seen cluster.
  }
  return false
}

/** Returns true if the path SHOULD be rejected (revisits a station). */
export function ruleNoRevisit(edges: readonly Edge[]): boolean {
  if (edges.length === 0) return false
  const landings = new Map<string, number>()
  landings.set(edges[0].from.id, 1)
  for (const e of edges) {
    if (e.type === EdgeType.Ride || e.type === EdgeType.WalkTransfer) {
      landings.set(e.to.id, (landings.get(e.to.id) ?? 0) + 1)
    }
    // WithinStationTransfer: no new landing
  }
  for (const v of landings.values()) if (v > 1) return true
  return false
}
